import type { CompanyRegistrationValues } from "../model/registrationTypes";
import { CompanyType, WhatToRegister } from "../model/registrationEnums";

const cleanObjectEntries = (value: object): object => {
  const entries = Object.entries(value)
    .filter(([key, v]) => key !== "_rowId" && v !== "")
    .map(([key, v]) => [key, withoutEmptyValues(v)])
    .filter(([, v]) => {
      if (Array.isArray(v)) return (v as unknown[]).length > 0;
      if (v && typeof v === "object")
        return Object.keys(v as object).length > 0;
      return true;
    });
  return Object.fromEntries(entries);
};

function withoutEmptyValues<T>(value: T): T {
  if (Array.isArray(value)) {
    return value
      .map(withoutEmptyValues)
      .filter(
        (item) =>
          typeof item !== "object" ||
          item === null ||
          Object.keys(item).length > 0,
      ) as T;
  }

  if (value && typeof value === "object") {
    return cleanObjectEntries(value) as T;
  }

  return value;
}

/** Meta fields plus the nested object for the currently selected flow only. */
export const buildCompanyRegistrationSubmitPayload = (
  values: CompanyRegistrationValues,
) => {
  const { whatToRegister, companyType } = values;

  if (whatToRegister === WhatToRegister.Termination) {
    return {
      whatToRegister,
      companyType,
      termination: withoutEmptyValues({
        ...values.termination,
        pNumbers: values.termination.pNumbers.map((pNumber) => pNumber.value),
      }),
    };
  }

  if (whatToRegister === WhatToRegister.Change) {
    const base = { whatToRegister, companyType };
    if (companyType === CompanyType.Interessentskab) {
      return {
        ...base,
        changeInteressentskab: withoutEmptyValues(values.changeInteressentskab),
      };
    }
    if (companyType === CompanyType.Advokatselskab) {
      return {
        ...base,
        changeAdvokatselskab: withoutEmptyValues(values.changeAdvokatselskab),
      };
    }
    return base;
  }

  if (whatToRegister === WhatToRegister.Creation) {
    const base = { whatToRegister, companyType };
    if (companyType === CompanyType.Enkeltmandsvirksomhed) {
      return {
        ...base,
        enkeltmandsvirksomhed: withoutEmptyValues(values.enkeltmandsvirksomhed),
      };
    }
    if (companyType === CompanyType.Interessentskab) {
      return {
        ...base,
        interessentskab: withoutEmptyValues(values.interessentskab),
      };
    }
    if (companyType === CompanyType.Advokatselskab) {
      return {
        ...base,
        advokatselskab: withoutEmptyValues(values.advokatselskab),
      };
    }
    return base;
  }

  return { whatToRegister, companyType };
};

export type CompanyRegistrationSubmitPayload = ReturnType<
  typeof buildCompanyRegistrationSubmitPayload
>;
